// libraries.
import { Col } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";

// hooks.
import { useInventoryPricingMutation } from "@store/apiSlices/inventoryPricing/inventoryPricing.endpoints";
import { useUserProfile } from "@shared/GoogleLoginButton/user-slice/useUserProfile.hook";
import { useNid } from "@pages/HomePage";

// components.
import { PriceInventorySummaryCO } from "./PriceInventorySummaryCO/PriceInventorySummaryCO";
import { DiagnosisCardsCO } from "./CardsSummaryInfoCO/DiagnosisCardsCO";
import { inventoryPriceType } from "../type";
import { usePostVimMutation } from "@domains/VIM/services/post-vim.endpoints";
import {
  getSelectedOption,
  getSuggestionOption,
  getVIMMetrics,
} from "@domains/VIM/slices/vim.slice";
import { useInventorySubmit } from "../shared/hooks/useInventorySubmit";
import { useHandleSuccess } from "../shared/hooks/useHandleSuccess";

export interface InventorySummaryForm {
  applyPercentageToPrices: boolean;
  commentInput: string;
  commissionPercentage: number;
  isLoading: boolean;
  leader: string;
  minPublicationPrice: number;
  publicationPrice: number;
  utilityCurrent: number | null;
  utilityInitial: number | null;
}

export const InventorySummaryCO = () => {
  const paths = useParams();
  const { user } = useUserProfile();
  const { nid } = useNid();
  const selectedOption = useSelector(getSelectedOption);
  const suggestionOption = useSelector(getSuggestionOption);
  const vimMetrics = useSelector(getVIMMetrics);

  const selectedNid = paths.nid ? paths.nid : nid;

  const [handlePostInventoryPricing, { isSuccess, isError }] =
    useInventoryPricingMutation();

  const [handleVIMLog] = usePostVimMutation();

  const { onSubmit } = useInventorySubmit({
    handlePostInventoryPricing,
    handleVIMLog,
    inventoryPriceType,
    nidSelected: Number(selectedNid),
    selectedOption,
    suggestionOption,
    user,
    vimMetrics,
  });

  const methods = useForm<InventorySummaryForm>({
    defaultValues: {
      applyPercentageToPrices: true,
      commentInput: "",
      commissionPercentage: 0,
      isLoading: false,
      leader: "",
      minPublicationPrice: 0,
      publicationPrice: 0,
      utilityCurrent: null,
      utilityInitial: null,
    },
    mode: "all",
  });

  useHandleSuccess(isSuccess, isError);

  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          data-testid="inventory-form"
          className="h-100 d-flex p-0"
        >
          <Col
            md={9}
            className="p-4 price-info  d-flex flex-column gap-4 overflow-y-auto"
            data-testid="inventory-info"
            style={{ height: "calc(100vh - 50px)" }}
          >
            <DiagnosisCardsCO />
          </Col>
          <Col
            md={3}
            className="p-4 price-summary text-white overflow-y-auto"
            data-testid="inventory-summary"
            style={{ height: "calc(100vh - 50px)" }}
          >
            <PriceInventorySummaryCO />
          </Col>
        </form>
      </FormProvider>
    </>
  );
};
